import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Mounted once at the application root. Handles three things:
 *
 *  1. Registers the service worker (`/sw.js`) on first paint, in browsers
 *     that support it. Skips registration on http:// (other than localhost),
 *     during SSR, and inside the Vite dev server — where SW caching would
 *     fight HMR.
 *  2. Suppresses the browser's own install banner (`beforeinstallprompt`).
 *     The site shows no install prompt of its own: the bottom-right corner
 *     belongs to the Mediavine Universal Player, and a mobile install bar
 *     would sit on the adhesion ad. Installing stays available from the
 *     browser menu (see /read-barcode-app).
 *  3. Detects when the registered SW finds a new build waiting, and shows
 *     a non-blocking "Reload to update" toast that calls the new SW's
 *     SKIP_WAITING message and reloads.
 */
export function PwaManager() {
  const [updateReady, setUpdateReady] = useState(false);
  const waitingSwRef = useRef<ServiceWorker | null>(null);

  // 1. Service worker registration ----------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    // Don't register in the Vite dev server. SW caching fights HMR and
    // produces confusing "why is my code stale" moments.
    const isLocalDev =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (isLocalDev) return;

    let cancelled = false;

    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((registration) => {
        if (cancelled) return;

        // Surface an already-waiting SW right away (e.g. user returned to a
        // tab where an update was queued during the previous session).
        if (registration.waiting && navigator.serviceWorker.controller) {
          waitingSwRef.current = registration.waiting;
          setUpdateReady(true);
        }

        // Watch the registration for a new SW being installed.
        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              // A new SW has installed *and* an existing controller exists →
              // this is an update, not first install.
              waitingSwRef.current = installing;
              setUpdateReady(true);
            }
          });
        });
      })
      .catch(() => {
        /* SW registration is best-effort; failures don't break the site. */
      });

    // When the active SW changes (after we send SKIP_WAITING), reload so
    // the new build takes over.
    let reloading = false;
    const onControllerChange = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  // 2. Browser install banner -----------------------------------------
  useEffect(() => {
    const onBeforeInstall = (e: Event) => e.preventDefault();
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  // 3. Update reload ------------------------------------------------------
  const applyUpdate = useCallback(() => {
    const waiting = waitingSwRef.current;
    if (waiting) {
      waiting.postMessage({ type: "SKIP_WAITING" });
    } else {
      // Fall through to a hard reload if the waiting ref was lost (rare,
      // e.g. tab reopened from history).
      window.location.reload();
    }
  }, []);

  if (!updateReady) return null;

  return (
    <div
      // Centred just below the sticky header (h-16 + border). The bottom of
      // the screen belongs to Mediavine: the adhesion ad along the bottom and
      // the Universal Player in the bottom-right corner.
      className="pointer-events-none fixed inset-x-0 top-[4.75rem] z-[60] flex flex-col items-center gap-2 px-4"
      aria-live="polite"
    >
      {updateReady && (
        <div
          role="status"
          className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-lg border border-border bg-background p-3 shadow-lg sm:max-w-sm"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <RefreshCw className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">New version available</div>
            <p className="mt-0.5 text-xs text-muted-foreground">Reload to use the latest build.</p>
          </div>
          <button
            type="button"
            onClick={applyUpdate}
            className="inline-flex shrink-0 items-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            Reload
          </button>
        </div>
      )}
    </div>
  );
}
